/**
 * Reglages de partie : mode, categories, manches, durees, taille de zone,
 * plafond d'effectif.
 *
 * Le meme bloc sert a DEUX endroits, et ce n'est pas une economie de lignes :
 * le salon avant de lancer, et l'ecran de FIN, ou l'hote prepare la revanche.
 * Sans lui a l'ecran de fin, le seul appel a l'action etait REJOUER, qui se lit
 * « la meme chose encore une fois » : il fallait avoir vote la revanche, donc
 * etre deja reparti, pour decouvrir au salon qu'on pouvait changer de mode. Un
 * jeu qui en a sept ne peut pas les cacher derriere un aller-retour.
 *
 * Il s'affiche pour TOUT LE MONDE, desactive pour qui n'est pas hote. Les
 * autres joueurs doivent voir a quoi ils s'engagent quand ils votent, et c'est
 * la raison d'etre de `useChangeFlash` : un reglage modifie a l'autre bout de
 * la table changerait sinon en silence.
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import {
  roundsRange,
  drawTimeRange,
  guessTimeRange,
  isCollab,
  SELECTABLE_GAME_MODES,
  ZONE_SIZE_OPTIONS,
  MAX_PLAYERS,
  MIN_PLAYERS,
  ROUNDS_MAX,
  ROUNDS_MIN,
  ROUNDS_OPTIONS,
  CYCLES_OPTIONS,
  countsCycles,
  hasFixedRounds,
  totalRounds,
  isContest,
  type GameMode,
  type RoomSnapshot,
} from '@retusch/shared';
import { useT } from '../locales/useT.js';
import type { TranslationKey } from '../locales/index.js';
import { connection } from '../net/connection.js';
import { CategoryPicker } from '../ui/CategoryPicker.js';

export function GameSettings({
  room,
  canEdit,
}: {
  readonly room: RoomSnapshot;
  readonly canEdit: boolean;
}): ReactElement {
  const t = useT();
  const cycles = countsCycles(room.settings.mode);
  // Le nom local qui suit evite de reecrire les huit `disabled` du bloc.
  const canEditSettings = canEdit;

  // Le collaboratif a sa propre plage de manches : une manche y est un cran de
  // rotation, donc un tableau de plus pour chacun. `null` ailleurs, ou les
  // regles historiques s'appliquent.
  const roundsOwn = roundsRange(room.settings.mode);
  const drawRange = drawTimeRange(room.settings.mode);
  const voteRange = guessTimeRange(room.settings.mode);

  return (
    <>
      {/* SELECTABLE_GAME_MODES et non GAME_MODES : l'ordre reste celui du
          protocole, les deux modes gratuits en tete puis les autres, mais on
          n'y propose que ce qui est jouable de bout en bout. */}
      <Segmented
        label={t('lobby.mode')}
        // Aucun badge sur les modes : tant que rien ne se vend et qu'aucune
        // date de bascule n'est fixee, un cadeau « Gratuit jusqu'au... »
        // annoncerait une echeance qui n'existe pas. Tout est gratuit, point.
        // Le badge revient (PremiumBadge, garde pour ca) le jour ou l'achat
        // s'ouvre, avec sa date.
        options={SELECTABLE_GAME_MODES.map((value) => ({
          value,
          label: t(MODE_LABEL[value]),
        }))}
        value={room.settings.mode}
        disabled={!canEditSettings}
        onChange={(mode) => connection.send({ type: 'settings', mode })}
      />
      <p className="segmented__hint">{t(MODE_HINT[room.settings.mode])}</p>

      {/* Categories d'images : tout est coche au depart, on retire ce dont
          on ne veut pas. Le bloc vit juste sous le mode, parce que c'est
          l'autre reglage qui change ce qu'on voit a l'ecran. */}
      <CategoryPicker
        selected={room.settings.categories}
        available={room.availableCategories}
        disabled={!canEditSettings}
        onChange={(categories) => connection.send({ type: 'settings', categories })}
      />

      {/* Pas de reglage de manches en battle royale : la partie s'y arrete
          quand il ne reste qu'un joueur, jamais a un compte fixe. Le laisser
          affiche proposait un choix que le jeu ignore ensuite en silence, ce
          qui est pire qu'une absence. `hasFixedRounds` porte deja cette regle,
          c'est elle qui decide aussi de l'affichage « manche n/total ». */}
      {hasFixedRounds(room.settings.mode) && (
        <>
        {/* En « un seul dessine », le reglage compte des TOURS COMPLETS : un tour
            = un dessin par joueur, sans quoi la partie ne serait pas equitable.

            Le selecteur affiche donc le nombre de manches REEL sur chaque bouton
            (6 · 12 · 18 a six joueurs), et l'intitule dit d'ou il sort. Il
            montrait « 1 · 2 · 3 » avec la conversion en note grise dessous :
            quelqu'un a regle trois tours a deux personnes, six joueurs sont
            arrives, et la partie a dure dix-huit manches sans que personne ne
            relise la note. Le chiffre qui coute doit etre celui qu'on lit.

            Et AUCUNE valeur libre ici, seul reglage dans ce cas : ce qu'on
            saisirait ne serait pas ce qu'on jouerait, puisque le cout d'un tour
            depend de l'effectif. */}
        <Segmented
          label={
            cycles
              ? t('lobby.cyclesLabel', {
                  rounds: totalRounds(room.settings.mode, room.settings.rounds, room.players.length),
                  cycles: room.settings.rounds,
                })
              : t('lobby.rounds')
          }
          options={(roundsOwn?.options ?? (cycles ? CYCLES_OPTIONS : ROUNDS_OPTIONS)).map(
            (value) => ({
              value,
              label: String(totalRounds(room.settings.mode, value, room.players.length)),
            }),
          )}
          value={room.settings.rounds}
          disabled={!canEditSettings}
          {...(cycles || roundsOwn !== null
            ? {}
            : { custom: { min: ROUNDS_MIN, max: ROUNDS_MAX } })}
          onChange={(rounds) => connection.send({ type: 'settings', rounds })}
        />
        </>
      )}

      {/* Les plages viennent du MODE : le collaboratif dessine court et vote
          long, et n'accepte pas de valeur libre pour le dessin. Une seule table
          en decide, celle que le serveur lit aussi. */}
      <Segmented
        label={t('lobby.drawTime')}
        options={drawRange.options.map((value) => ({
          value,
          label: t('lobby.seconds', { n: value }),
        }))}
        value={room.settings.drawTime}
        disabled={!canEditSettings}
        {...(drawRange.free
          ? { custom: { min: drawRange.min, max: drawRange.max, unit: 's' } }
          : {})}
        onChange={(drawTime) => connection.send({ type: 'settings', drawTime })}
      />
      {/* En concours, ce chrono n'est plus un temps de recherche mais un
          temps de vote : l'intitule doit dire ce qu'il commande. */}
      <Segmented
        label={
          isContest(room.settings.mode) || isCollab(room.settings.mode)
            ? t('lobby.voteTime')
            : t('lobby.guessTime')
        }
        options={voteRange.options.map((value) => ({
          value,
          label: t('lobby.seconds', { n: value }),
        }))}
        value={room.settings.guessTime}
        disabled={!canEditSettings}
        {...(voteRange.free
          ? { custom: { min: voteRange.min, max: voteRange.max, unit: 's' } }
          : {})}
        onChange={(guessTime) => connection.send({ type: 'settings', guessTime })}
      />
      {ZONE_SIZE_SELECTABLE && (
        <>
          <Segmented
            label={t('lobby.zoneSize')}
            options={ZONE_SIZE_OPTIONS.map((value) => ({
              value,
              label: `${Math.round(value * 100)}%`,
            }))}
            value={room.settings.zoneSize}
            disabled={!canEditSettings}
            onChange={(zoneSize) => connection.send({ type: 'settings', zoneSize })}
          />
          <p className="segmented__hint">{t('lobby.zoneSizeHint')}</p>
        </>
      )}

      {/* Plafond d'effectif : surtout utile en salon public, ou des
          inconnus arrivent. Un compteur plutot qu'un rail de neuf cases :
          la valeur se regle par petits pas, elle ne se choisit pas dans une
          liste. */}
      <Stepper
        label={t('lobby.maxPlayers')}
        value={room.settings.maxPlayers}
        // Jamais sous l'effectif deja present : le serveur refuserait, et
        // on n'expulse personne pour honorer un reglage.
        min={Math.max(MIN_PLAYERS, room.players.length)}
        max={MAX_PLAYERS}
        disabled={!canEditSettings}
        decrementLabel={t('lobby.maxPlayersLess')}
        incrementLabel={t('lobby.maxPlayersMore')}
        onChange={(maxPlayers) => connection.send({ type: 'settings', maxPlayers })}
      />
      <p className="segmented__hint">{t('lobby.maxPlayersHint')}</p>
    </>
  );
}

/**
 * Signale visuellement qu'une valeur vient de changer. Sert aux joueurs qui ne
 * sont pas hote : sans ce flash, un reglage modifie a l'autre bout de la table
 * change en silence et personne ne le remarque.
 */
function useChangeFlash(value: unknown): boolean {
  const [flashing, setFlashing] = useState(false);
  const previous = useRef(value);

  useEffect(() => {
    if (previous.current === value) return;
    previous.current = value;
    setFlashing(true);
    const timer = window.setTimeout(() => setFlashing(false), 150);
    return () => window.clearTimeout(timer);
  }, [value]);

  return flashing;
}

/**
 * Selecteur de taille de zone : MASQUE, pas supprime. La zone varie desormais
 * par categorie (`zoneSideFor`), un pourcentage unique a l'ecran mentirait sur
 * deux categories sur sept. Le reglage existe toujours dans le protocole et le
 * serveur ; le reafficher est cette constante a passer a `true`.
 */
export const ZONE_SIZE_SELECTABLE = false;

/** Libelles des modes, dans l'ordre de GAME_MODES. */
export const MODE_LABEL: Record<GameMode, TranslationKey> = {
  hunt: 'lobby.modeHunt',
  classic: 'lobby.modeClassic',
  oneDraws: 'lobby.modeOneDraws',
  battle: 'lobby.modeBattle',
  stream: 'lobby.modeStream',
  contest: 'lobby.modeContest',
  collab: 'lobby.modeCollab',
};

const MODE_HINT: Record<GameMode, TranslationKey> = {
  hunt: 'lobby.modeHuntHint',
  classic: 'lobby.modeClassicHint',
  oneDraws: 'lobby.modeOneDrawsHint',
  battle: 'lobby.modeBattleHint',
  stream: 'lobby.modeStreamHint',
  contest: 'lobby.modeContestHint',
  collab: 'lobby.modeCollabHint',
};

/**
 * Compteur a deux boutons. Pour une valeur qu'on ajuste d'un cran plutot qu'on
 * ne choisit dans une liste : neuf cases cote a cote pour un effectif, c'est un
 * rail illisible qui deborde sur telephone.
 */
function Stepper({
  label,
  value,
  min,
  max,
  disabled,
  decrementLabel,
  incrementLabel,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  disabled: boolean;
  decrementLabel: string;
  incrementLabel: string;
  onChange: (value: number) => void;
}): ReactElement {
  const flashing = useChangeFlash(value);
  const clamp = (next: number): number => Math.min(Math.max(next, min), max);

  return (
    <div className="segmented" data-flash={flashing}>
      <p className="section-label">{label}</p>
      <div className="stepper" role="group" aria-label={label}>
        <button
          type="button"
          className="stepper__button"
          aria-label={decrementLabel}
          disabled={disabled || value <= min}
          onClick={() => onChange(clamp(value - 1))}
        >
          −
        </button>
        <output className="stepper__value mono-tabular" aria-live="polite">
          {value}
        </output>
        <button
          type="button"
          className="stepper__button"
          aria-label={incrementLabel}
          disabled={disabled || value >= max}
          onClick={() => onChange(clamp(value + 1))}
        >
          +
        </button>
      </div>
    </div>
  );
}

function Segmented<T extends string | number>({
  label,
  options,
  value,
  disabled,
  onChange,
  custom,
}: {
  label: string;
  options: ReadonlyArray<{ value: T; label: string; badge?: ReactNode }>;
  value: T;
  disabled: boolean;
  onChange: (value: T) => void;
  /**
   * Plage libre, en plus des raccourcis. Presente comme une case de plus dans
   * le meme rail : c'est le meme reglage, il ne merite pas un second bloc.
   */
  custom?: { min: number; max: number; unit?: string };
}): ReactElement {
  const t = useT();
  const flashing = useChangeFlash(value);
  const isPreset = options.some((option) => option.value === value);
  // Ouvert des que la valeur courante n'est aucun des raccourcis : sinon un
  // reglage personnalise recu d'un autre joueur serait invisible.
  const [open, setOpen] = useState(!isPreset);
  const [draft, setDraft] = useState(String(value));

  useEffect(() => {
    if (!isPreset) setOpen(true);
    setDraft(String(value));
  }, [value, isPreset]);

  const commit = useCallback(
    (raw: string) => {
      if (custom === undefined) return;
      const parsed = Number.parseInt(raw, 10);
      if (!Number.isFinite(parsed)) return;
      // Borne ici AUSSI, pas seulement cote serveur : une valeur hors plage
      // ferait rejeter le message entier, et le reglage semblerait ignore.
      const clamped = Math.min(Math.max(parsed, custom.min), custom.max);
      setDraft(String(clamped));
      onChange(clamped as T);
    },
    [custom, onChange],
  );

  return (
    <div className="segmented" data-flash={flashing}>
      <p className="section-label">{label}</p>
      <div className="segmented__track" role="group" aria-label={label}>
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            className="segmented__option"
            aria-pressed={option.value === value && !open}
            disabled={disabled}
            onClick={() => {
              setOpen(false);
              onChange(option.value);
            }}
          >
            {option.label}
            {option.badge}
          </button>
        ))}
        {custom !== undefined && (
          <button
            type="button"
            className="segmented__option"
            aria-pressed={open}
            disabled={disabled}
            onClick={() => setOpen(true)}
            title={`${custom.min} a ${custom.max}`}
          >
            {t('lobby.custom')}
          </button>
        )}
      </div>

      {custom !== undefined && open && (
        <div className="segmented__custom">
          <input
            type="number"
            className="field__input segmented__number mono-tabular"
            min={custom.min}
            max={custom.max}
            step={1}
            value={draft}
            disabled={disabled}
            aria-label={label}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={(event) => commit(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') commit((event.target as HTMLInputElement).value);
            }}
          />
          {custom.unit !== undefined && <span className="segmented__unit">{custom.unit}</span>}
          <span className="segmented__range">
            {custom.min} a {custom.max}
          </span>
        </div>
      )}
    </div>
  );
}

